import { useState } from 'react';

import BugReportIcon from '@mui/icons-material/BugReport';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import {
  Badge,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { saveAs } from 'file-saver';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import InputFileUpload from 'shared/components/Input/InputUploadFile/InputFileUpload';
import { APIErrorT } from 'shared/models/api';
import api from 'utils/api';
import { formatDateFilename } from 'utils/date';

type ErrorsCSV = {
  txId: string;
  errorDevices: number;
  date: string;
}
interface DeviceConfigItemFormUploadCSVProps {
  flowId: string;
  profileId: string;
}
// CSV File Exemple :
// name,device_id,device_ip
// device99990,887766554433bbff,Fd44::2
// device99991,887766554433beff,Fd44::11
function DeviceConfigItemFormUploadCSV({
  flowId,
  profileId,
}: DeviceConfigItemFormUploadCSVProps) {
  const [hasErrors, setHasErrors] = useState<ErrorsCSV | null>(null);
  const [isDownloadingErrorReport, setIsDownloadingErrorReport] = useState(false);
  const history = useHistory();

  const onCSVFileLoaded = (csvData: any) => {
    api.deviceConfig.uploadCSV(
      flowId,
      profileId,
      csvData,
    )
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.warn(error?.message);
            });
        } else {
          resp.json()
            .then((data: ErrorsCSV) => {
              if (data.errorDevices > 0) {
                setHasErrors(data);
                toast.error('Failed to create the devices - Check the report');
                return;
              }
              toast.success('Successfully created the devices');
              history.push(`/flows/${flowId}`);
            });
        }
      });
  };

  const downloadCSVErrorReport = () => {
    setIsDownloadingErrorReport(true);
    api.deviceConfig.downloadCSVErrorReport(hasErrors?.txId as string)
      .then((resp: Response) => {
        if (resp.status >= 400) {
          toast.error(`Failed to download the error report - Error ${resp.status} ${resp.statusText}`);
          setIsDownloadingErrorReport(false);
        } else {
          resp.blob()
            .then((data: Blob) => {
              try {
                saveAs(data, `report-${hasErrors?.errorDevices}-errors-${formatDateFilename(hasErrors?.date as string)}.csv`);
              } catch {
                toast.error('Failed to load in browser the error report');
              }
              setIsDownloadingErrorReport(false);
            });
        }
      });
  };

  return (
    <div className="form__section" style={{ marginTop: '1rem' }}>
      <div
        className="actions-side-by-side"
        style={{
          height: '6rem',
          marginLeft: '8px',
        }}
      >
        <InputFileUpload
          title="Upload CSV File"
          onFileLoaded={onCSVFileLoaded}
          variant="outlined"
          icon={<FilePresentIcon />}
          acceptedFiles=".csv"
        />
        {
          hasErrors && (
            <Tooltip
              title={`Download the report - ${hasErrors.errorDevices} errors`}
              arrow
              placement="left"
            >
              <Badge badgeContent={hasErrors.errorDevices} color="warning">
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => downloadCSVErrorReport()}
                  disabled={isDownloadingErrorReport}
                >
                  {
                    isDownloadingErrorReport
                      ? <CircularProgress color="warning" size="3rem" />
                      : <BugReportIcon />
                  }
                </Button>
              </Badge>
            </Tooltip>
          )
        }
      </div>
    </div>
  );
}

export default DeviceConfigItemFormUploadCSV;
